import { DonutChart, type DonutSlice } from "@/company/components/DonutChart";
import { PaceGauge } from "@/company/components/PaceGauge";
import { TeamPaceBars } from "@/company/components/TeamPaceBars";
import { useDisplayCompanyData } from "@/company/hooks/useDisplayCompanyData";
import { salesDepartment } from "@/grid/config/departments/sales";
import { format } from "date-fns";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

/** Layout is drawn on a fixed 1080p canvas and scaled to the player, so 720p or 4:3 TVs never clip. */
const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;

function viewportFit() {
  const scale = Math.min(window.innerWidth / CANVAS_WIDTH, window.innerHeight / CANVAS_HEIGHT);
  return {
    scale,
    left: (window.innerWidth - CANVAS_WIDTH * scale) / 2,
    top: (window.innerHeight - CANVAS_HEIGHT * scale) / 2,
  };
}

function FitToScreen({ children }: { children: ReactNode }) {
  const [fit, setFit] = useState(viewportFit);

  useEffect(() => {
    const onResize = () => setFit(viewportFit());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-[var(--color-muted)] text-[var(--color-foreground)]">
      <div
        className="absolute"
        style={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          left: fit.left,
          top: fit.top,
          transform: `scale(${fit.scale})`,
          transformOrigin: "top left",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** One fixed TV tile: title + body that fills the remaining height. */
function TvCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex min-h-0 flex-col rounded-3xl border border-[var(--color-border)] bg-[var(--color-surface)] px-10 pb-8 pt-7 shadow-sm">
      <h2 className="text-2xl font-semibold text-[var(--color-text)]">{title}</h2>
      <div className="flex min-h-0 flex-1 items-center justify-center">{children}</div>
    </section>
  );
}

function TvMessage({ children }: { children: ReactNode }) {
  return (
    <div className="grid-bg flex min-h-dvh items-center justify-center p-6">
      <p className="max-w-md text-center text-lg text-[var(--color-muted-foreground)]">
        {children}
      </p>
    </div>
  );
}

/**
 * Company Sales TV (Yodeck): the top four cards of /company/sales, token-gated, no login.
 * Bookmark: /display/company/sales?token=<GRID_DISPLAY_TOKEN>
 */
export function DisplayCompanyPage() {
  const { departmentId = "sales" } = useParams<{ departmentId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { data, isLoading, isError, error } = useDisplayCompanyData(salesDepartment, token);

  useEffect(() => {
    document.title = "Brand Blvd: Sales Overview";
  }, []);

  const donutSlices: DonutSlice[] = useMemo(
    () =>
      (data?.teams ?? []).map((team) => ({
        key: team.colorKey,
        label: team.teamName,
        value: team.qtdActual,
        color: team.color,
        sharePct: team.qtdSharePct,
      })),
    [data?.teams],
  );

  if (departmentId !== "sales") {
    return <Navigate to={`/display/company/sales?${searchParams.toString()}`} replace />;
  }

  if (!token) {
    return (
      <TvMessage>
        Missing display token. Add <code className="text-sm">?token=...</code> to the URL.
      </TvMessage>
    );
  }

  if (isError && !data) {
    return <TvMessage>{error?.message ?? "Unable to load company data."}</TvMessage>;
  }

  if (isLoading || !data) {
    return <TvMessage>Loading...</TvMessage>;
  }

  return (
    <FitToScreen>
      <div className="flex h-full flex-col gap-6 px-10 py-7">
        <header className="flex shrink-0 flex-col items-center gap-1">
          <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">Sales Overview</h1>
          <span className="text-lg text-[var(--color-muted-foreground)]">
            {format(new Date(), "EEEE, MMMM d")}
          </span>
        </header>

        <main className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-6">
          <TvCard title="QTD Sales">
            <PaceGauge summary={data.qtd} variant="tv" showTitle={false} />
          </TvCard>
          <TvCard title="YTD Sales">
            <PaceGauge summary={data.ytd} variant="tv" showTitle={false} />
          </TvCard>
          <TvCard title="QTD contribution">
            <DonutChart slices={donutSlices} size="tv" />
          </TvCard>
          <TvCard title="Team QTD pace">
            {data.teams.length > 0 ? (
              <TeamPaceBars teams={data.teams} size="tv" className="w-full" />
            ) : (
              <p className="text-xl text-[var(--color-text-muted)]">No active sales teams found.</p>
            )}
          </TvCard>
        </main>
      </div>
    </FitToScreen>
  );
}
